import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
  Tabs,
  Tab,
  CardActions,
  Divider,
  IconButton,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Security as SecurityIcon,
  Gavel as GavelIcon,
  Business as BusinessIcon,
  Masks as MasksIcon,
  LocalPolice as LocalPoliceIcon,
  School as SchoolIcon,
  AttachMoney as AttachMoneyIcon,
  Handshake as HandshakeIcon,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoOutlinedIcon,
  Bookmark as BookmarkIcon,
  AutoAwesome as AutoAwesomeIcon,
  Close as CloseIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import ProModal from '../../common/ProModal';

const FactionsEditor = ({ 
  factions = [], 
  onAddFaction, 
  onEditFaction, 
  onDeleteFaction 
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const cardBg = isDarkMode ? 'rgba(35, 5, 5, 0.9)' : '#fff';
  const accentColor = isDarkMode ? '#ff8a80' : '#b71c1c';
  const accentBg = isDarkMode ? 'rgba(244, 67, 54, 0.2)' : 'rgba(244, 67, 54, 0.05)';
  const accentBorder = isDarkMode ? 'rgba(255, 138, 128, 0.35)' : 'rgba(244, 67, 54, 0.15)';
  const chipBg = isDarkMode ? 'rgba(229, 115, 115, 0.2)' : 'rgba(244, 67, 54, 0.12)';
  const chipHover = isDarkMode ? 'rgba(229, 115, 115, 0.32)' : 'rgba(244, 67, 54, 0.22)';
  // New state for custom templates
  const [customTemplates, setCustomTemplates] = useState([]);
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  const [newTemplateIcon, setNewTemplateIcon] = useState('guild');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFaction, setSelectedFaction] = useState(null);
  const [newFaction, setNewFaction] = useState({
    name: '',
    description: '',
    details: '',
    tags: [],
    importance: 'medium',
    category: 'factions',
    goals: '',
    structure: '',
    resources: '',
    allies: '',
    enemies: ''
  });

  const importanceLevels = [
    { value: 'low', label: 'Low', color: 'default' },
    { value: 'medium', label: 'Medium', color: 'primary' },
    { value: 'high', label: 'High', color: 'secondary' },
    { value: 'critical', label: 'Critical', color: 'error' }
  ];

  // Alignment options for factions
  const alignments = [
    { value: 'lawful_good', label: 'Lawful Good' },
    { value: 'neutral_good', label: 'Neutral Good' },
    { value: 'chaotic_good', label: 'Chaotic Good' },
    { value: 'lawful_neutral', label: 'Lawful Neutral' },
    { value: 'true_neutral', label: 'True Neutral' },
    { value: 'chaotic_neutral', label: 'Chaotic Neutral' },
    { value: 'lawful_evil', label: 'Lawful Evil' },
    { value: 'neutral_evil', label: 'Neutral Evil' },
    { value: 'chaotic_evil', label: 'Chaotic Evil' }
  ];

  // Faction types for manual selection
  const factionTypes = [
    { value: 'guild', label: 'Guild', icon: <BusinessIcon fontSize="small" /> },
    { value: 'government', label: 'Government', icon: <GavelIcon fontSize="small" /> },
    { value: 'military', label: 'Military', icon: <SecurityIcon fontSize="small" /> },
    { value: 'criminal', label: 'Criminal', icon: <MasksIcon fontSize="small" /> },
    { value: 'religious', label: 'Religious', icon: <PeopleIcon fontSize="small" /> },
    { value: 'academic', label: 'Academic', icon: <SchoolIcon fontSize="small" /> },
    { value: 'mercantile', label: 'Mercantile', icon: <AttachMoneyIcon fontSize="small" /> },
    { value: 'diplomatic', label: 'Diplomatic', icon: <HandshakeIcon fontSize="small" /> },
    { value: 'law', label: 'Law Enforcement', icon: <LocalPoliceIcon fontSize="small" /> },
    { value: 'custom', label: 'Custom Type', icon: <InfoOutlinedIcon fontSize="small" /> },
  ];

  // Faction templates - optimized for film/screenwriting with cinematic details
  const factionTemplates = [
    { 
      name: 'guild', 
      label: 'Professional Guild', 
      icon: <BusinessIcon fontSize="small" />,
      description: 'Masters of their craft who guard secrets worth killing for',
      data: {
        importance: 'medium',
        description: 'A brotherhood of craftsmen who control their trade with iron grip. Their secrets are passed in whispers, and outsiders who learn too much disappear.',
        goals: 'Monopolize their craft. Crush competition. Train the worthy, destroy the rest. Accumulate wealth and influence behind closed doors.',
        structure: 'Apprentices who serve for years before learning anything real. Journeymen who do the work. Masters who hold the secrets. The Guildmaster whose word is law.',
        resources: 'Trade secrets worth fortunes. Workshops that double as meeting halls. Connections to every merchant and noble who needs their services. A network of informants.',
        allies: 'Nobles who need their products. Merchants who move their goods. City officials who take their bribes.',
        enemies: 'Rival guilds encroaching on their territory. Black market craftsmen undercutting prices. Reformers who want to break their monopoly.',
        details: '**Visual Identity:** Guild symbols on doors and clothing. Secret hand signs. Workshops lit by forge-fire or candlelight.\n\n**Cinematic Moments:** The initiation ritual. The guild meeting where a traitor is exposed. The master teaching the final secret.\n\n**Conflict Hooks:** A guild secret has been stolen. An apprentice has been murdered. The guild is being squeezed by a more powerful faction.',
        mood: 'Secretive and protective. Pride in craft masking ruthless business.',
        visualStyle: 'Workshops and back rooms. Hands at work. Symbols hidden in plain sight.',
        characterTypes: 'The ambitious apprentice, the master with a secret, the guildmaster playing politics, the outsider trying to break in.'
      }
    },
    { 
      name: 'government', 
      label: 'The Regime', 
      icon: <GavelIcon fontSize="small" />,
      description: 'The official power—corrupt, crumbling, or both',
      data: {
        importance: 'high',
        description: 'The legitimate authority, for whatever that\'s worth. They make the laws, collect the taxes, and decide who lives and dies. Everyone else operates in their shadow.',
        goals: 'Maintain power at any cost. Keep the populace compliant. Crush dissent before it spreads. Enrich those at the top while keeping the machine running.',
        structure: 'The ruler (visible or puppet). The inner circle who actually make decisions. Bureaucrats who implement policy. Enforcers who ensure compliance.',
        resources: 'The treasury (however empty). The military and police. Legal authority to imprison or execute. Spies in every corner. The weight of tradition.',
        allies: 'Wealthy families who benefit from stability. Religious leaders who preach obedience. Foreign powers with aligned interests.',
        enemies: 'Revolutionaries. Ambitious nobles. Criminal organizations that rival their power. The people, when pushed too far.',
        details: '**Visual Identity:** Flags and uniforms. Imposing government buildings. The contrast between palace luxury and street poverty.\n\n**Cinematic Moments:** The throne room audience. The secret council meeting. The public execution. The moment the crowd turns.\n\n**Conflict Hooks:** Succession crisis. Invasion threat. Internal coup brewing. A secret that could bring it all down.',
        mood: 'The weight of power. Paranoia at the top. The machinery of state grinding on.',
        visualStyle: 'Marble halls and guarded doors. Documents and seals. The view from above.',
        characterTypes: 'The idealistic reformer, the cynical survivor, the true believer, the ruler losing grip, the power behind the throne.'
      }
    },
    { 
      name: 'military', 
      label: 'Military Order', 
      icon: <SecurityIcon fontSize="small" />,
      description: 'Warriors bound by oath, honor, and the chain of command',
      data: {
        importance: 'high',
        description: 'Trained killers with a code. They follow orders, protect their own, and do what civilians can\'t stomach. The uniform means something—even when it shouldn\'t.',
        goals: 'Victory. Honor. Survival of the unit. Following orders—or knowing when not to. Protecting what matters, whatever the cost.',
        structure: 'Ranks that mean life or death. Officers who command. Veterans who actually know. Recruits who learn fast or die. The general who sees the big picture.',
        resources: 'Weapons and training. Fortifications and supply lines. Intelligence networks. The loyalty of soldiers who\'ve bled together.',
        allies: 'The government they serve (usually). Weapons suppliers. Veterans\' families. Civilians who sleep safe because of them.',
        enemies: 'Enemy forces. Politicians who waste their lives. Traitors within. The ghosts of those they couldn\'t save.',
        details: '**Visual Identity:** Uniforms and insignia. Weapons maintained with care. Scars and medals. The thousand-yard stare.\n\n**Cinematic Moments:** The briefing before battle. The firefight. The moment of sacrifice. The veteran returning home changed.\n\n**Conflict Hooks:** Impossible orders. A soldier who knows too much. The enemy within. The mission that goes wrong.',
        mood: 'Brotherhood forged in blood. The weight of duty. Violence as profession.',
        visualStyle: 'Formations and fortifications. The calm before battle. Aftermath.',
        characterTypes: 'The by-the-book officer, the cynical sergeant, the idealistic recruit, the soldier with a secret, the one who\'s seen too much.'
      }
    },
    { 
      name: 'criminal', 
      label: 'Criminal Syndicate', 
      icon: <MasksIcon fontSize="small" />,
      description: 'The shadow economy—where loyalty is everything and betrayal is death',
      data: {
        importance: 'high',
        description: 'They run what the law forbids. Drugs, gambling, protection, flesh—whatever pays. Cross them and you disappear. Join them and you\'re family. Until you\'re not.',
        goals: 'Control the territory. Eliminate competition. Corrupt the authorities. Make money—and make sure everyone knows who\'s really in charge.',
        structure: 'The boss who everyone fears. Lieutenants who run operations. Soldiers who do the dirty work. Associates who can be sacrificed. The code that holds it together.',
        resources: 'Cash—lots of it. Weapons and muscle. Corrupt cops and politicians. Front businesses. Secrets on everyone who matters.',
        allies: 'Corrupt officials on the payroll. Legitimate businesses that launder money. Other organizations with complementary interests.',
        enemies: 'Law enforcement (the honest ones). Rival organizations. Ambitious underlings. Anyone who talks.',
        details: '**Visual Identity:** Expensive suits in cheap bars. Tattoos that tell stories. The back room where deals are made. Violence that sends a message.\n\n**Cinematic Moments:** The sit-down between bosses. The hit. The interrogation. The moment someone decides to flip.\n\n**Conflict Hooks:** A new boss takes over. Someone\'s skimming. A witness needs to disappear. The feds are closing in.',
        mood: 'Paranoid loyalty. Wealth built on blood. The family you can\'t leave.',
        visualStyle: 'Neon and shadow. Cash and guns. The contrast between public face and private violence.',
        characterTypes: 'The ambitious underboss, the enforcer with a code, the boss going soft, the one who wants out, the rat.'
      }
    },
    { 
      name: 'religious', 
      label: 'Religious Order', 
      icon: <PeopleIcon fontSize="small" />,
      description: 'True believers with power—salvation or damnation, they decide',
      data: {
        importance: 'high',
        description: 'They speak for the divine—or claim to. Faith gives them power over souls, and that translates to power over everything else. Doubt is dangerous. Heresy is death.',
        goals: 'Spread the faith. Maintain orthodoxy. Accumulate power and wealth for the greater glory. Destroy heresy wherever it hides.',
        structure: 'The high priest who interprets divine will. The inner circle who guard doctrine. Priests who tend the flock. Inquisitors who hunt the unfaithful. The faithful masses.',
        resources: 'Temples and sacred sites. Tithes from believers. Ancient texts and relics. The fear of damnation. Networks of the faithful everywhere.',
        allies: 'Devout rulers. The faithful among the powerful. Other orders that share their beliefs.',
        enemies: 'Heretics and apostates. Rival faiths. Secular powers that limit their authority. The doubters within.',
        details: '**Visual Identity:** Sacred symbols and ceremonial robes. Temples that inspire awe. The contrast between public piety and private power.\n\n**Cinematic Moments:** The grand ceremony. The private confession. The inquisition. The crisis of faith.\n\n**Conflict Hooks:** A schism threatens. A relic has been stolen. A heresy is spreading. The high priest has a secret.',
        mood: 'Absolute certainty masking doubt. Power dressed as humility. Faith as weapon.',
        visualStyle: 'Candlelight and incense. Soaring architecture. Crowds of the faithful. The lone doubter.',
        characterTypes: 'The true believer, the political priest, the secret doubter, the inquisitor, the reformer.'
      }
    },
    { 
      name: 'rebels', 
      label: 'Rebel Movement', 
      icon: <PeopleIcon fontSize="small" />,
      description: 'Freedom fighters or terrorists—depends who\'s telling the story',
      data: {
        importance: 'high',
        description: 'They\'ve had enough. The system is broken, corrupt, evil—and they\'re going to tear it down. Heroes to some, criminals to others. They know they might not survive.',
        goals: 'Overthrow the current order. Free the oppressed. Build something better—or at least destroy what exists. Survive long enough to see victory.',
        structure: 'Cells that don\'t know each other. Leaders who inspire. Soldiers who believe. Sympathizers who provide cover. The inner circle who know the real plan.',
        resources: 'Safehouses and hidden caches. Weapons smuggled or stolen. The support of the oppressed. Idealism that keeps them fighting.',
        allies: 'The common people (sometimes). Foreign powers with their own agendas. Defectors from the regime. Anyone who hates the current order.',
        enemies: 'The regime and its enforcers. Informants and traitors. Rival factions with different visions. Their own extremists.',
        details: '**Visual Identity:** Hidden symbols and secret signs. Improvised weapons. Faces covered or revealed depending on the moment. The contrast between hiding and striking.\n\n**Cinematic Moments:** The secret meeting. The attack. The interrogation that doesn\'t break them. The moment of victory—or defeat.\n\n**Conflict Hooks:** A traitor in the ranks. The plan that requires sacrifice. The leader who\'s losing faith. Victory that costs too much.',
        mood: 'Desperate hope. Brotherhood of the hunted. The price of resistance.',
        visualStyle: 'Shadows and safehouses. The moment before action. Faces lit by firelight.',
        characterTypes: 'The charismatic leader, the true believer, the one who joined for personal reasons, the spy, the one who goes too far.'
      }
    },
    { 
      name: 'spies', 
      label: 'Intelligence Network', 
      icon: <MasksIcon fontSize="small" />,
      description: 'The shadow war—where truth is a weapon and everyone lies',
      data: {
        importance: 'high',
        description: 'They know secrets that could topple governments. They operate in shadows, wearing masks even with each other. Trust no one. Verify everything. Burn what you can\'t use.',
        goals: 'Gather intelligence. Protect secrets. Manipulate events from the shadows. Eliminate threats before they materialize. Stay invisible.',
        structure: 'Handlers who run agents. Analysts who piece together puzzles. Field operatives who take the risks. The director who sees the whole board. Assets who don\'t know who they really work for.',
        resources: 'Information networks spanning nations. Blackmail material. False identities. Safe houses. Technology for surveillance and elimination.',
        allies: 'The government they serve (officially). Assets in every organization. Other agencies when interests align.',
        enemies: 'Enemy intelligence services. Targets who\'ve figured it out. Politicians who want to control them. Their own people who\'ve been turned.',
        details: '**Visual Identity:** Ordinary appearances hiding extraordinary capabilities. Dead drops and coded messages. The moment the mask slips.\n\n**Cinematic Moments:** The recruitment. The extraction. The interrogation. The betrayal. The handler who cares too much.\n\n**Conflict Hooks:** An agent has gone dark. A mole is leaking secrets. An operation is blown. The truth about an old mission surfaces.',
        mood: 'Paranoid professionalism. Lies within lies. The cost of living in shadows.',
        visualStyle: 'Surveillance footage. Ordinary places with hidden significance. The moment of recognition.',
        characterTypes: 'The burned spy, the handler with a conscience, the analyst who sees the pattern, the double agent, the one who wants out.'
      }
    },
    { 
      name: 'mercenary', 
      label: 'Mercenary Company', 
      icon: <SecurityIcon fontSize="small" />,
      description: 'Professional soldiers for hire—loyalty lasts as long as the contract',
      data: {
        importance: 'medium',
        description: 'They fight for whoever pays. No flags, no causes—just contracts. Professional, effective, and gone when the money runs out. Everyone needs them. Nobody trusts them.',
        goals: 'Complete the contract. Get paid. Maintain reputation. Survive to spend the money. Maybe find something worth fighting for.',
        structure: 'The captain who takes contracts. Officers who lead in the field. Veterans who train the new blood. Specialists for every job. The company is family—until the contract says otherwise.',
        resources: 'Weapons and equipment. Combat experience. Reputation that opens doors. Contacts who need violence done professionally.',
        allies: 'Current employers. Arms dealers. Other companies when contracts align. Veterans of past campaigns.',
        enemies: 'Current targets. Past employers who didn\'t pay. Rivals undercutting their prices. Idealists who hate what they represent.',
        details: '**Visual Identity:** Practical gear, personal touches. Scars and stories. The camp between battles. Wealth spent fast.\n\n**Cinematic Moments:** The contract negotiation. The battle. The moral line they won\'t cross—or will. The job that changes everything.\n\n**Conflict Hooks:** A contract against former allies. An employer who won\'t pay. A job that\'s not what it seemed. The one who wants to go straight.',
        mood: 'Professional violence. Cynicism masking codes of honor. Money as the only honest motivation.',
        visualStyle: 'Weapons and gear. The calm before action. Counting money after.',
        characterTypes: 'The captain with a past, the idealist who ended up here, the one who loves the work, the veteran counting days, the new recruit.'
      }
    },
    { 
      name: 'cult', 
      label: 'Secret Cult', 
      icon: <MasksIcon fontSize="small" />,
      description: 'True believers in something the world isn\'t ready to know',
      data: {
        importance: 'medium',
        description: 'They\'ve seen the truth—or been shown it. Now they serve powers others don\'t believe in. Secrecy is survival. The uninitiated wouldn\'t understand. The initiated can never leave.',
        goals: 'Serve their hidden masters. Recruit the worthy. Prepare for the revelation. Protect the secrets at any cost. Bring about what was promised.',
        structure: 'The inner circle who know everything. Initiates who\'ve proven themselves. Outer members who know only pieces. The leader who speaks for what they serve.',
        resources: 'Hidden meeting places. Ancient texts and artifacts. Wealth accumulated over generations. Members in positions of power. Knowledge others would kill for.',
        allies: 'Members don\'t know who else serves. Unwitting pawns in positions of power. Others who serve the same masters.',
        enemies: 'Those who hunt them. Rival cults. Members who try to leave. Anyone who gets too close to the truth.',
        details: '**Visual Identity:** Symbols hidden in plain sight. Robes and masks for ceremonies. Ordinary faces hiding extraordinary beliefs.\n\n**Cinematic Moments:** The initiation ritual. The secret meeting. The sacrifice. The moment an outsider discovers the truth.\n\n**Conflict Hooks:** An initiate wants out. An outsider has seen too much. The prophecy is coming true. A schism over interpretation.',
        mood: 'Fanatical devotion. Secrets within secrets. The thrill of hidden knowledge.',
        visualStyle: 'Candlelit rituals. Symbols carved in hidden places. The ordinary concealing the terrible.',
        characterTypes: 'The true believer, the one who doubts, the leader who may be mad, the outsider who stumbles in, the hunter.'
      }
    },
    { 
      name: 'corporation', 
      label: 'Mega-Corporation', 
      icon: <AttachMoneyIcon fontSize="small" />,
      description: 'Profit above all—they own everything including the people who think they\'re free',
      data: {
        importance: 'high',
        description: 'They\'re not evil—they\'re efficient. Profit is the only metric that matters. Laws are obstacles to be lobbied away. People are resources to be optimized. The market decides who lives and dies.',
        goals: 'Maximize shareholder value. Eliminate competition. Control markets. Influence governments. Grow until there\'s nothing left to consume.',
        structure: 'The board that sets direction. Executives who implement. Managers who squeeze productivity. Workers who are replaceable. The CEO who embodies the brand.',
        resources: 'Capital that dwarfs nations. Lawyers and lobbyists. Private security. R&D that shapes the future. Data on everyone.',
        allies: 'Politicians they\'ve bought. Media they control. Other corporations when interests align. Consumers who can\'t imagine alternatives.',
        enemies: 'Regulators. Unions. Activists. Whistleblowers. Competitors. Anyone who threatens the bottom line.',
        details: '**Visual Identity:** Glass towers and branded everything. Suits and smiles. The contrast between marketing and reality.\n\n**Cinematic Moments:** The board meeting. The hostile takeover. The whistleblower\'s choice. The human cost revealed.\n\n**Conflict Hooks:** A product that kills. A merger that destroys lives. The executive who grows a conscience. The secret the PR can\'t spin.',
        mood: 'Cheerful ruthlessness. Profit as morality. The machine that runs on people.',
        visualStyle: 'Corporate aesthetics. The view from the top floor. The factory floor. The spreadsheet that decides fates.',
        characterTypes: 'The true believer executive, the whistleblower, the fixer, the worker who sees too much, the heir who questions.'
      }
    },
  ];

  const handleOpenDialog = (faction = null) => {
    if (faction) {
      setSelectedFaction(faction);
      setNewFaction({
        ...faction,
        tags: faction.tags || []
      });
    } else {
      setSelectedFaction(null);
      setNewFaction({
        name: '',
        description: '',
        details: '',
        tags: [],
        importance: 'medium',
        category: 'factions',
        goals: '',
        structure: '',
        resources: '',
        allies: '',
        enemies: '',
        type: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSaveFaction = () => {
    const factionToSave = {
      ...newFaction,
      id: selectedFaction ? selectedFaction.id : Date.now().toString()
    };

    if (selectedFaction) {
      onEditFaction(factionToSave);
    } else {
      onAddFaction(factionToSave);
    }

    handleCloseDialog();
  };

  // Function to apply faction template
  const applyFactionTemplate = (templateName) => {
    const template = factionTemplates.find(t => t.name === templateName);
    if (template) {
      // Build cinematic details from new fields
      const cinematicDetails = template.data.details + 
        (template.data.mood ? `\n\n**Mood:** ${template.data.mood}` : '') +
        (template.data.visualStyle ? `\n\n**Visual Style:** ${template.data.visualStyle}` : '') +
        (template.data.characterTypes ? `\n\n**Character Archetypes:** ${template.data.characterTypes}` : '');
      
      setNewFaction({
        ...newFaction,
        description: template.data.description,
        goals: template.data.goals,
        structure: template.data.structure,
        resources: template.data.resources,
        allies: template.data.allies,
        enemies: template.data.enemies,
        details: cinematicDetails,
        importance: template.data.importance,
        type: template.name
      });
    }
  };

  // Function to save the current faction as a template
  const saveAsTemplate = () => {
    if (!newTemplateName.trim()) {
      return;
    }
    
    // Create a new template from the current faction data
    const newTemplate = {
      name: `custom-${Date.now()}`,
      label: newTemplateName,
      icon: factionTemplates.find(t => t.name === newTemplateIcon)?.icon || <PeopleIcon fontSize="small" />,
      description: newTemplateDescription || 'Custom faction template',
      data: {
        importance: newFaction.importance || 'medium',
        description: newFaction.description || '',
        goals: newFaction.goals || '',
        structure: newFaction.structure || '',
        resources: newFaction.resources || '',
        allies: newFaction.allies || '',
        enemies: newFaction.enemies || '',
        details: newFaction.details || ''
      },
      isCustom: true
    };
    
    setCustomTemplates([...customTemplates, newTemplate]);
    setShowSaveTemplateDialog(false);
    setNewTemplateName('');
    setNewTemplateDescription('');
    setNewTemplateIcon('guild');
  };
  
  // Function to apply a custom template
  const applyCustomTemplate = (templateName) => {
    const template = customTemplates.find(t => t.name === templateName);
    if (template) {
      setNewFaction({
        ...newFaction,
        description: template.data.description,
        goals: template.data.goals,
        structure: template.data.structure,
        resources: template.data.resources,
        allies: template.data.allies,
        enemies: template.data.enemies,
        details: template.data.details,
        importance: template.data.importance
      });
    }
  };
  
  // Function to delete a custom template
  const deleteCustomTemplate = (templateName) => {
    setCustomTemplates(customTemplates.filter(t => t.name !== templateName));
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Tooltip title="Add new faction" arrow>
          <span>
            <IconButton
              onClick={() => handleOpenDialog()}
              aria-label="Add new faction"
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                color: accentColor,
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(244,67,54,0.12)'
                }
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant="h5" component="h2" sx={{ 
          fontWeight: 600,
          color: accentColor
        }}>
          Factions
        </Typography>
      </Box>

      {factions.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            textAlign: 'center',
            borderRadius: 2,
            border: `1px dashed ${accentBorder}`,
            bgcolor: isDarkMode ? 'rgba(183, 28, 28, 0.16)' : 'rgba(244, 67, 54, 0.06)'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: accentColor }}>
            No factions added yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tap the “+” icon to add your first faction and map power dynamics.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {factions.map(faction => (
            <Grid item xs={12} sm={6} md={4} key={faction.id}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                borderRadius: 1,
                overflow: 'hidden',
                boxShadow: isDarkMode ? '0 12px 30px rgba(0,0,0,0.55)' : '0 4px 12px rgba(0,0,0,0.08)',
                transition: 'transform 0.3s, box-shadow 0.3s',
                border: `1px solid ${isDarkMode ? 'rgba(255,138,128,0.3)' : 'rgba(0,0,0,0.05)'}`,
                backgroundColor: cardBg,
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: isDarkMode ? '0 18px 40px rgba(0,0,0,0.7)' : '0 12px 20px rgba(0,0,0,0.12)'
                }
              }}>
                <Box sx={{ 
                  p: 1, 
                  background: isDarkMode ? 'linear-gradient(135deg, rgba(239,83,80,0.35), rgba(183,28,28,0.15))' : 'linear-gradient(135deg, #f4433640, #f4433610)',
                  borderBottom: `1px solid ${isDarkMode ? 'rgba(255,138,128,0.25)' : 'rgba(0,0,0,0.05)'}`
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600,
                      color: accentColor
                    }}>
                      {faction.name}
                    </Typography>
                    {faction.importance && (
                      <Chip 
                        size="small" 
                        label={faction.importance.charAt(0).toUpperCase() + faction.importance.slice(1)} 
                        color={
                          faction.importance === 'high' ? 'error' : 
                          faction.importance === 'medium' ? 'primary' : 
                          'default'
                        }
                        sx={{ 
                          fontWeight: 500,
                          height: '22px'
                        }}
                      />
                    )}
                  </Box>
                </Box>
                <Box sx={{ p: 2, flexGrow: 1 }}>
                  {faction.description && (
                    <Typography variant="body2" sx={{ mb: 1, color: isDarkMode ? 'rgba(255,255,255,0.8)' : 'text.secondary' }}>
                      {faction.description}
                    </Typography>
                  )}
                  
                  {faction.goals && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="body2" sx={{ mr: 0.5, fontWeight: 600, color: accentColor }}>
                        Goals:
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        color: isDarkMode ? 'rgba(255,255,255,0.85)' : 'text.secondary'
                      }}>
                        {faction.goals}
                      </Typography>
                    </Box>
                  )}
                  
                  {faction.structure && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="body2" sx={{ mr: 0.5, fontWeight: 600, color: accentColor }}>
                        Structure:
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        color: isDarkMode ? 'rgba(255,255,255,0.85)' : 'text.secondary'
                      }}>
                        {faction.structure}
                      </Typography>
                    </Box>
                  )}
                  
                  {faction.allies && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="body2" sx={{ mr: 0.5, fontWeight: 600, color: accentColor }}>
                        Allies:
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        color: isDarkMode ? 'rgba(255,255,255,0.8)' : 'text.secondary'
                      }}>
                        {faction.allies}
                      </Typography>
                    </Box>
                  )}
                  
                  {faction.enemies && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="body2" sx={{ mr: 0.5, fontWeight: 600, color: accentColor }}>
                        Enemies:
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        color: isDarkMode ? 'rgba(255,255,255,0.8)' : 'text.secondary'
                      }}>
                        {faction.enemies}
                      </Typography>
                    </Box>
                  )}
                  
                  {faction.tags && faction.tags.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {faction.tags.map((tag, index) => (
                        <Chip 
                          key={index} 
                          label={tag} 
                          size="small" 
                          sx={{ 
                            height: '20px', 
                            fontSize: '0.7rem',
                            bgcolor: chipBg,
                            color: accentColor
                          }} 
                        />
                      ))}
                    </Box>
                  )}
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  p: 1, 
                  pt: 0,
                  borderTop: '1px solid rgba(0,0,0,0.05)'
                }}>
                  <Button 
                    size="small" 
                    startIcon={<EditIcon />} 
                    onClick={() => handleOpenDialog(faction)}
                    sx={{ mr: 1 }}
                  >
                    Edit
                  </Button>
                  <Button 
                    size="small" 
                    startIcon={<DeleteIcon />} 
                    color="error"
                    onClick={() => onDeleteFaction(faction.id)}
                  >
                    Delete
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <ProModal
        open={openDialog}
        onClose={handleCloseDialog}
        title={selectedFaction ? 'Edit Faction' : 'Add New Faction'}
        eyebrow="FACTIONS"
        icon={<PeopleIcon />}
        maxWidth="lg"
        actions={(
          <>
            <Button 
              onClick={handleCloseDialog} 
              color="inherit"
              variant="outlined"
              startIcon={<CloseIcon />}
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none',
                mr: 1
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveFaction} 
              variant="contained"
              startIcon={<SaveIcon />}
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none',
                background: 'linear-gradient(120deg, #f44336, #b71c1c)',
                '&:hover': {
                  background: 'linear-gradient(120deg, #d32f2f, #880e4f)'
                }
              }}
            >
              {selectedFaction ? 'Update Faction' : 'Add Faction'}
            </Button>
          </>
        )}
        contentProps={{ sx: { pt: 3, px: 3 } }}
      >
        <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: accentBg, borderRadius: 1, border: `1px solid ${accentBorder}` }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: accentColor, display: 'flex', alignItems: 'center' }}>
                  <InfoOutlinedIcon sx={{ mr: 1, fontSize: '1rem' }} /> Basic Information
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Faction Name"
                      value={newFaction.name}
                      onChange={(e) => setNewFaction({ ...newFaction, name: e.target.value })}
                      fullWidth
                      required
                      placeholder="Give your faction a memorable name"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PeopleIcon sx={{ color: accentColor }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                      <InputLabel id="importance-label">Narrative Importance</InputLabel>
                      <Select
                        labelId="importance-label"
                        value={newFaction.importance}
                        label="Narrative Importance"
                        onChange={(e) => setNewFaction({ ...newFaction, importance: e.target.value })}
                      >
                        {importanceLevels.map((level) => (
                          <MenuItem key={level.value} value={level.value}>
                            {level.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        How significant is this faction to your story?
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Brief Description"
                      value={newFaction.description}
                      onChange={(e) => setNewFaction({ ...newFaction, description: e.target.value })}
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="A logline for this faction—who are they and why should we fear/respect them?"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                  </Grid>
                </Grid>
              </Paper>
              
              <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: accentBg, borderRadius: 1, border: `1px solid ${accentBorder}` }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: accentColor, display: 'flex', alignItems: 'center' }}>
                  <PeopleIcon sx={{ mr: 1, fontSize: '1rem' }} /> Faction Classification
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                      <InputLabel id="type-label">Faction Type</InputLabel>
                      <Select
                        labelId="type-label"
                        value={newFaction.type}
                        label="Faction Type"
                        onChange={(e) => setNewFaction({ ...newFaction, type: e.target.value })}
                      >
                        {factionTypes.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        What category of faction is this?
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                      <InputLabel id="alignment-label">Alignment</InputLabel>
                      <Select
                        labelId="alignment-label"
                        value={newFaction.alignment}
                        label="Alignment"
                        onChange={(e) => setNewFaction({ ...newFaction, alignment: e.target.value })}
                      >
                        {alignments.map((alignment) => (
                          <MenuItem key={alignment.value} value={alignment.value}>
                            {alignment.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        What is this faction's moral stance?
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                </Grid>
              </Paper>
              <Paper elevation={0} sx={{ p: 2, bgcolor: accentBg, borderRadius: 1, border: `1px solid ${accentBorder}` }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: accentColor, display: 'flex', alignItems: 'center' }}>
                  <GavelIcon sx={{ mr: 1, fontSize: '1rem' }} /> Faction Details
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Goals and Motivations"
                      value={newFaction.goals || ''}
                      onChange={(e) => setNewFaction({ ...newFaction, goals: e.target.value })}
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="What do they want? What will they do to get it? What's the conflict potential?"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' }, mb: 2 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <GavelIcon sx={{ color: accentColor }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Structure and Organization"
                      value={newFaction.structure || ''}
                      onChange={(e) => setNewFaction({ ...newFaction, structure: e.target.value })}
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="Power structure and hierarchy. Who gives orders? Who follows? Where's the tension?"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' }, mb: 2 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BusinessIcon sx={{ color: accentColor }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Resources and Assets"
                      value={newFaction.resources || ''}
                      onChange={(e) => setNewFaction({ ...newFaction, resources: e.target.value })}
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="Money, weapons, information, people—what gives them power?"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' }, mb: 2 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AttachMoneyIcon sx={{ color: accentColor }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: accentBg, borderRadius: 1, border: `1px solid ${accentBorder}` }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: accentColor, display: 'flex', alignItems: 'center' }}>
                  <HandshakeIcon sx={{ mr: 1, fontSize: '1rem' }} /> Relationships
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Allies"
                      value={newFaction.allies || ''}
                      onChange={(e) => setNewFaction({ ...newFaction, allies: e.target.value })}
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="Who do they work with? Alliances of convenience or true loyalty? What could break the alliance?"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' }, mb: 2 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <HandshakeIcon sx={{ color: accentColor }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Enemies"
                      value={newFaction.enemies || ''}
                      onChange={(e) => setNewFaction({ ...newFaction, enemies: e.target.value })}
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="Who do they oppose? Old grudges or active conflicts? What would it take to make peace?"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' }, mb: 2 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SecurityIcon sx={{ color: accentColor }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
              
              <Accordion 
                defaultExpanded={false}
                sx={{ 
                  bgcolor: accentBg, 
                  borderRadius: '1 !important', 
                  border: `1px solid ${accentBorder}`,
                  '&:before': { display: 'none' },
                  boxShadow: 'none'
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon sx={{ color: accentColor }} />}
                  sx={{ 
                    minHeight: 48,
                    '& .MuiAccordionSummary-content': { my: 1 }
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: accentColor, display: 'flex', alignItems: 'center' }}>
                    <AutoAwesomeIcon sx={{ mr: 1, fontSize: '1rem' }} /> Quick Templates
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Apply a template to quickly fill in common faction types.
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: accentColor }}>
                      Standard Templates
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {factionTemplates.map((template) => (
                        <Chip
                          key={template.name}
                          icon={<PeopleIcon />}
                          label={template.label}
                          onClick={() => applyFactionTemplate(template.name)}
                          sx={{
                            bgcolor: chipBg,
                            color: accentColor,
                            '&:hover': {
                              bgcolor: chipHover,
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                  
                  {customTemplates && customTemplates.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: accentColor }}>
                        Custom Templates
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {customTemplates.map((template) => (
                          <Chip
                            key={template.name}
                            icon={<BookmarkIcon />}
                            label={template.name}
                            onClick={() => applyCustomTemplate(template.name)}
                            sx={{
                              bgcolor: chipBg,
                              color: accentColor,
                              '&:hover': {
                                bgcolor: chipHover,
                              }
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                  
                  <Box sx={{ mt: 3 }}>
                    <Button 
                      variant="contained" 
                      fullWidth
                      onClick={() => setShowSaveTemplateDialog(true)}
                      startIcon={<SaveIcon />}
                      sx={{ 
                        borderRadius: '8px',
                        textTransform: 'none',
                        background: 'linear-gradient(120deg, #f44336, #b71c1c)',
                        '&:hover': {
                          background: 'linear-gradient(120deg, #d32f2f, #880e4f)'
                        }
                      }}
                    >
                      Save Current as Template
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Grid>
        </Grid>
      </ProModal>

      <ProModal
        open={showSaveTemplateDialog}
        onClose={() => setShowSaveTemplateDialog(false)}
        title="Save Template"
        eyebrow="FACTIONS"
        icon={<BookmarkIcon />}
        maxWidth="sm"
        actions={(
          <>
            <Button 
              onClick={() => setShowSaveTemplateDialog(false)}
              color="inherit"
              variant="outlined"
              startIcon={<CloseIcon />}
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none',
                mr: 1
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={saveAsTemplate}
              variant="contained"
              disabled={!newTemplateName.trim()}
              startIcon={<SaveIcon />}
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none',
                background: 'linear-gradient(120deg, #f44336, #b71c1c)',
                '&:hover': {
                  background: 'linear-gradient(120deg, #d32f2f, #880e4f)'
                }
              }}
            >
              Save Template
            </Button>
          </>
        )}
        contentProps={{ sx: { pt: 3, px: 3 } }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Save the current faction as a reusable template for future worlds.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Template Name"
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              fullWidth
              placeholder="Enter a name for your template"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BookmarkIcon sx={{ color: accentColor }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Template Description"
              value={newTemplateDescription}
              onChange={(e) => setNewTemplateDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="What makes this faction template unique?"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
              <InputLabel id="template-icon-label">Template Icon</InputLabel>
              <Select
                labelId="template-icon-label"
                value={newTemplateIcon}
                label="Template Icon"
                onChange={(e) => setNewTemplateIcon(e.target.value)}
              >
                {factionTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </ProModal>
    </Box>
  );
};

export default FactionsEditor;
